class ProductAdviser {
	constructor(form) {
		this.form = form;
		this.currentStep = 1;
		this.totalSteps = 0;
		this.selectedItems = {};
		this.steps = Array.from(this.form.querySelectorAll('.product-adviser__step'));
		this.breadcrumbs = Array.from(this.form.querySelectorAll('.product-adviser__breadcrumb-item'));
		this.button = this.form.querySelector('.product-adviser__footer-button');
		this.progressBar = this.form.querySelector('.product-adviser__progress-bar');
		this.progressTooltip = this.form.querySelector('.product-adviser__progress-tooltip');
		this.footer = this.form.querySelector('.product-adviser__footer');
		this.button.disabled = true;
		this.titles = [];
		this.penIcon = '';
		this.sourceCollection = '';
		this.fallbackUrl = '';
	}

	init = () => {
		this.button.addEventListener('click', this.handleSubmit);

		this.form.addEventListener('change', this.handleFormChange);
		this.breadcrumbs.forEach((breadcrumb) => {
			breadcrumb.addEventListener('click', this.handleBreadcrumbClick);
		});
		this.updateProgressBar();
	}

	setTotalSteps = (totalSteps) => {
		this.totalSteps = totalSteps;
	}

	setPenIcon = (penIcon) => {
		this.penIcon = penIcon;
	}

	setTitles = (titles) => {
		this.titles = titles;
	}

	setSourceCollection = (sourceCollection) => {
		this.sourceCollection = sourceCollection;
	}

	setFallbackUrl = (fallbackUrl) => {
		this.fallbackUrl = fallbackUrl;
	}

	handleSubmit = (event) => {
		event.preventDefault();
		if (this.currentStep == this.totalSteps) {
			this.submitForm();
		} else {
			this.nextStep();
		}
	}

	handleBreadcrumbClick = (event) => {
		const breadcrumb = event.target.closest('.product-adviser__breadcrumb-item');
		this.breadcrumbs.forEach(item => item.classList.remove('selected'));
		breadcrumb.classList.add('selected');
		this.currentStep = parseInt(breadcrumb.dataset.step);
		this.showStepContent(this.currentStep);
		this.checkAndDisableButton();
		this.updateBreadcrumb();
		this.updateProgressBar();
	}

	handleFormChange = (event) => {
		this.checkAndDisableButton();
		this.updateSelectedItems(event);
	}

	hideFooter = () => {
		this.footer.style.display = 'none';
	}

	showFooter = () => {
		this.footer.style.display = 'flex';
	}

	updateSelectedItems = (event) => {
		const selection = event.target.closest('.product-adviser__list-item').querySelector('input[type="hidden"]');
		const isMultiple = event.target.type === 'checkbox';
		const title = this.titles[this.currentStep - 1];

		if (isMultiple) {
			this.selectedItems[title] = this.selectedItems[title] || [];
			if (this.selectedItems[title].indexOf(selection.value) === -1) {
				this.selectedItems[title].push(selection.value);
			} else {
				const index = this.selectedItems[title].indexOf(selection.value);
				this.selectedItems[title].splice(index, 1);
			}
		} else {
			this.selectedItems[title] = selection.value;
		}
	}

	checkAndDisableButton = () => {
		this.button.removeAttribute('aria-busy');
		const currentStep = this.steps[this.currentStep - 1];
		const currentStepOptions = currentStep.querySelectorAll('.product-adviser__input input');
		const isAnyOptionSelected = Array.from(currentStepOptions).some(option => option.checked);
		this.button.disabled = !isAnyOptionSelected;
	}

	updateBreadcrumb = () => {
		this.breadcrumbs.forEach(breadcrumb => {
			breadcrumb.classList.remove('selected');
			if (breadcrumb.dataset.step == this.currentStep) {
				breadcrumb.classList.add('selected');
				breadcrumb.classList.add('visible');
			}
		});
	}

	showStepContent = (stepNumber) => {
		this.steps.forEach(step => step.classList.remove('active'));
		this.steps[stepNumber - 1].classList.add('active');
		this.showFooter();
	}

	updateProgressBar = () => {
		const completedSteps = Object.values(this.selectedItems)
			.filter(item => {
				if (Array.isArray(item)) {
					return item.length > 0;
				}
				return item !== '';
			}).length;
		const progress = (completedSteps / this.totalSteps) * 100;
		this.progressBar.style.width = `${progress}%`;
		this.progressTooltip.textContent = `${Math.round(progress)}%`;
		this.progressTooltip.style.opacity = '1';

		if (this.tooltipTimeout) {
			clearTimeout(this.tooltipTimeout);
		}
		this.tooltipTimeout = setTimeout(() => {
			this.progressTooltip.style.opacity = '0';
		}, 2000);
	}

	nextStep = () => {
		this.currentStep++;
		this.showStepContent(this.currentStep);
		this.checkAndDisableButton();
		this.updateBreadcrumb();
		this.updateProgressBar();
		this.scrollToSection();
	}

	showFinalStep = () => {
		this.steps.forEach(step => step.classList.remove('active'));
		const finalStep = this.form.querySelector('.product-adviser__step.final-step');
		const selection = finalStep.querySelector('.product-adviser__selection');
		selection.innerHTML = '';

		for (let [key, value] of Object.entries(this.selectedItems)) {
			const content = Array.isArray(value) ? value.join(', ') : value;
			const item = document.createElement('div');
			item.classList.add('product-adviser__selection-item');
			item.innerHTML = `<span>${key}: ${content}</span>${this.penIcon}`;
			item.dataset.step = this.titles.indexOf(key) + 1;
			item.addEventListener('click', () => {
				this.currentStep = parseInt(item.dataset.step);
				this.showStepContent(this.currentStep);
				this.checkAndDisableButton();
				this.updateBreadcrumb();
				this.updateProgressBar();
			});
			selection.appendChild(item);
		}

		finalStep.classList.add('active');
		this.scrollToSection();
	}

	scrollToSection = () => {
		const section = this.form.closest('.product-adviser__container');
		const offset = 72;
		const elementPosition = section.getBoundingClientRect().top;
		const offsetPosition = elementPosition + window.pageYOffset - offset;
		window.scrollTo({
			top: offsetPosition,
			behavior: 'smooth'
		});
	}

	prepareFormData = () => {
		const formData = new FormData(this.form);
		const result = {};

		for (let [key, value] of formData.entries()) {
			if (key.endsWith('[]')) {
				const baseKey = key.slice(0, -2);
				result[baseKey] = result[baseKey] || '';
				result[baseKey] += `${result[baseKey] ? ',' : ''}${value}`;
			} else {
				result[key] = value;
			}
		}

		const searchParams = new URLSearchParams();

		for (let [key, value] of Object.entries(result)) {
			if (key.startsWith('selected_')) {
				continue;
			}

			const values = value.split(',');

			for (let item of values) {
				searchParams.append(key, item);
			}
		}

		return searchParams;
	}

	submitForm = async () => {
		const searchParams = this.prepareFormData();
		this.button.setAttribute('aria-busy', 'true');

		try {
			const response = await fetch(`${this.sourceCollection}?${searchParams.toString()}`);
			const html = await response.text();
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');
			let productList = doc.querySelector('.product-list__inner');

			if (!productList) {
				const fallbackResponse = await fetch(this.fallbackUrl);
				const fallbackHtml = await fallbackResponse.text();
				const fallbackDoc = parser.parseFromString(fallbackHtml, 'text/html');
				productList = fallbackDoc.querySelector('.product-list__inner');
			}

			if (productList) {
				const productItems = productList.querySelectorAll('.product-item');
				productItems.forEach(item => {
					item.style.opacity = 1;
				});
				document.querySelector('.product-list__inner').innerHTML = productList.innerHTML;
			}

			// Append static products inside the dynamic product list
			const staticProducts = document.querySelectorAll('.product-list__static-products .product-item');
			if (staticProducts) {
				const productListInner = document.querySelector('.product-list__inner');
				for (let product of staticProducts) {
					const _product = product.cloneNode(true);
					productListInner.appendChild(_product);
				}
			}

			this.updateProgressBar();
			this.showFinalStep();
			this.hideFooter();
		} catch (error) {
			console.error('Error fetching products:', error);
		} finally {
			this.button.removeAttribute('aria-busy');
		}
	}
}

window.ProductAdviser = ProductAdviser;
